import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Plus, ListFilter, EllipsisVertical } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../../../../components/shadcn/popover";
import { FilterRuleProps } from "../../../../main/component/filter/AdvancedFilter";
import FilterTag from "./FilterTag";
import FieldPicker from "./FieldPicker";

type FieldOption = {
  label: string;
  value: string;
  data: any;
};

type AdvancedFilterPanelProps = {
  fields: FieldOption[];
  rules: FilterRuleProps[];
  onChange: (index: number, rule: FilterRuleProps) => void;
  onAdd: (rule: FilterRuleProps) => void;
  onRemove: (index: number) => void;
  onClearAll: () => void;
};

/**
 * Floating advanced filter panel content.
 * Uses Radix UI Popover for the "Add filter" picker — no portal hacks needed.
 */
const AdvancedFilterPanelContent = ({
  fields,
  rules,
  onChange,
  onAdd,
  onRemove,
  onClearAll,
}: AdvancedFilterPanelProps) => {
  const { t } = useTranslation();

  const lastRule = rules[rules.length - 1];
  const isLastIncomplete =
    lastRule && (!lastRule.operator || !lastRule.fieldValue || lastRule.fieldValue.length === 0);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-5 space-y-3 min-w-[520px]">
      {/* Filter rows */}
      {rules.map((rule, idx) => (
        <FilterTag
          key={`${rule.fieldId}_${idx}`}
          fields={fields}
          rule={rule}
          index={idx}
          showLogic={true}
          showAdd={false}
          onChange={(r) => onChange(idx, r)}
          onRemove={() => onRemove(idx)}
        />
      ))}

      {/* Footer: + Add filter | Clear all filters */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <Popover>
          <PopoverTrigger asChild disabled={!!isLastIncomplete}>
            <button
              className={`inline-flex items-center gap-1.5 px-2 py-1 text-sm rounded-md transition-colors ${
                isLastIncomplete
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50 cursor-pointer"
              }`}
              disabled={!!isLastIncomplete}
            >
              <Plus className="w-3.5 h-3.5" />
              <span>{t("Add filter")}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={4}
            className="w-auto p-0 rounded-xl bg-white z-9999"
          >
            <FieldPicker
              fields={fields}
              onSelect={(rule) => {
                onAdd(rule);
              }}
              onClose={() => {}}
            />
          </PopoverContent>
        </Popover>

        <button
          className="text-sm text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
          onClick={onClearAll}
        >
          {t("Clear all filters")}
        </button>
      </div>
    </div>
  );
};

/**
 * Collapsed sidebar tag + floating wide panel.
 * Accepts `defaultOpen` so the panel auto-opens when transitioning from 1→2+ filters.
 */
export const AdvancedFilterTag = ({
  fields,
  rules,
  onChange,
  onAdd,
  onRemove,
  onClearAll,
  defaultOpen = false,
}: AdvancedFilterPanelProps & { defaultOpen?: boolean }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showClearMenu, setShowClearMenu] = useState(false);
  const tagRef = useRef<HTMLButtonElement>(null);
  const clearBtnRef = useRef<HTMLButtonElement>(null);

  // Defer auto-open until after the tag button is painted
  useEffect(() => {
    if (defaultOpen) {
      requestAnimationFrame(() => setOpen(true));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getPanelStyle = (): React.CSSProperties => {
    if (!tagRef.current) return {};
    const rect = tagRef.current.getBoundingClientRect();
    return {
      position: "fixed",
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
      zIndex: 9990,
    };
  };

  const getClearMenuStyle = (): React.CSSProperties => {
    if (!clearBtnRef.current) return {};
    const rect = clearBtnRef.current.getBoundingClientRect();
    return {
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.right - 160,
      zIndex: 9999,
    };
  };

  return (
    <div className="flex items-center gap-1.5">
      <button
        ref={tagRef}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer outline-none"
        onClick={() => setOpen(!open)}
      >
        <ListFilter className="w-3.5 h-3.5 text-gray-500" />
        <span>{t("Advanced filter")}</span>
        <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
          {rules.length}
        </span>
      </button>

      {/* Floating panel via portal */}
      {open &&
        createPortal(
          <div onMouseDown={(e) => e.stopPropagation()}>
            <div
              className="fixed inset-0"
              style={{ zIndex: 9989 }}
              onMouseDown={() => setOpen(false)}
            />
            <div style={getPanelStyle()}>
              <AdvancedFilterPanelContent
                fields={fields}
                rules={rules}
                onChange={onChange}
                onAdd={onAdd}
                onRemove={onRemove}
                onClearAll={() => {
                  onClearAll();
                  setOpen(false);
                }}
              />
            </div>
          </div>,
          document.body
        )}

      {/* ⋮ clear all menu */}
      <button
        ref={clearBtnRef}
        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors cursor-pointer outline-none"
        onClick={() => setShowClearMenu(!showClearMenu)}
      >
        <EllipsisVertical className="w-3.5 h-3.5" />
      </button>
      {showClearMenu &&
        createPortal(
          <div onMouseDown={(e) => e.stopPropagation()}>
            <div
              className="fixed inset-0"
              style={{ zIndex: 9998 }}
              onMouseDown={() => setShowClearMenu(false)}
            />
            <div
              style={getClearMenuStyle()}
              className="w-40 bg-white rounded-lg border border-gray-200 shadow-lg py-1"
            >
              <button
                className="w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                onClick={() => {
                  onClearAll();
                  setShowClearMenu(false);
                }}
              >
                {t("Clear all filters")}
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AdvancedFilterPanelContent;
